import React, { useEffect, useRef, useState } from 'react'
import { ImagePlus, Trash2 } from 'lucide-react'
import { companiesApi } from '../../lib/api'
import { useToast } from '../common/Toast'
import { useT } from '../../i18n'

/** The theme's own backdrops: warm white behind a picture in dark mode, the dashboard's dark in light mode */
export const DEFAULT_BACKDROP = { dark: '#f5f1ea', light: '#161614' } as const

/**
 * A picture's backdrops, from its address (the server puts them there: ?v=…&dark=default&light=1a2b3c) —
 * as the CSS variables .pic-backdrop paints in the dark and the light theme; undefined without any
 */
export function backdropStyle(url: string | null | undefined): React.CSSProperties | undefined {
  if (!url) return undefined
  let q: URLSearchParams
  try { q = new URL(url, 'http://x').searchParams } catch { return undefined }
  const colour = (theme: 'dark' | 'light') => {
    const v = q.get(theme)
    return v === 'default' ? DEFAULT_BACKDROP[theme] : v && /^[0-9a-f]{6}$/i.test(v) ? `#${v}` : null
  }
  const dark = colour('dark'), light = colour('light')
  if (!dark && !light) return undefined
  return { ...(dark ? { '--bd-dark': dark } : {}), ...(light ? { '--bd-light': light } : {}) } as React.CSSProperties
}

/** A company's logo (on its backdrop, if it has one for this theme), or its initial on a coloured square when it has none */
export function CompanyLogo({ url, name, size = 32 }: { url?: string | null; name: string; size?: number }) {
  const t = useT()
  if (url) return <img src={url} alt={t('settings.logo.alt', { name })} width={size} height={size} className="pic-backdrop"
    style={{ width: size, height: size, borderRadius: size / 4, objectFit: 'contain', flexShrink: 0, ['--pic-bg' as string]: 'var(--bg-3)', ...backdropStyle(url) }} />
  return (
    <div aria-hidden="true" style={{ width: size, height: size, borderRadius: size / 4, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(59,130,246,0.15)', color: '#60A5FA', fontWeight: 700, fontSize: size * 0.45 }}>
      {name.slice(0, 1).toUpperCase()}
    </div>
  )
}

/** the file as chosen: never sent, only read here (the scaled picture is what goes), so it may be large */
const MAX_FILE = 10 * 1024 * 1024
const SIDE = 256
/** what the server keeps (LOGO_MAX_BYTES) */
const MAX_LOGO = 256 * 1024
const bytesOf = (dataUrl: string) => Math.floor((dataUrl.length - dataUrl.indexOf(',') - 1) * 3 / 4)

/** the banner logo: the topbar shows it at most 32 px high; kept sharp up to this */
const BANNER = { width: 960, height: 192 }

/** The file as a data: address: the dashboard's image policy (img-src 'self' data: https:) allows no blob: */
const dataUrlOf = (file: File) => new Promise<string>((resolve, reject) => {
  const r = new FileReader()
  r.onload = () => resolve(String(r.result))
  r.onerror = () => reject(r.error ?? new Error('unreadable'))
  r.readAsDataURL(file)
})

/** An SVG's own proportions from its viewBox, for one that gives no width and height (its picture has no size) */
const viewBoxOf = (svg: string): { w: number; h: number } | null => {
  const m = /viewBox\s*=\s*["']\s*[-\d.eE]+[\s,]+[-\d.eE]+[\s,]+([\d.eE]+)[\s,]+([\d.eE]+)/.exec(svg)
  const w = m ? Number(m[1]) : NaN, h = m ? Number(m[2]) : NaN
  return w > 0 && h > 0 ? { w, h } : null
}

/**
 * Any picture this browser can open (PNG, JPEG, WebP, GIF, SVG, AVIF, BMP, ICO; HEIC and TIFF in
 * Safari), drawn here and sent as PNG: the logo in a 256×256 square, whole (letterboxed on a
 * transparent background) — the letters to suppliers (PDF) can carry PNG, not WebP; the banner whole
 * within 960×192, as wide as it is (a drawing, SVG, scaled up to fit). A picture so full of detail that
 * its PNG is over what the server keeps goes as JPEG instead, on white. The server never gets an SVG.
 */
async function scaleLogo(file: File, kind: 'logo' | 'banner' = 'logo'): Promise<string> {
  const url = await dataUrlOf(file)
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image()
    i.onload = () => resolve(i)
    i.onerror = () => reject(new Error('unreadable'))
    i.src = url
  })
  const box = kind === 'banner' ? BANNER : { width: SIDE, height: SIDE }
  const svg = file.type === 'image/svg+xml' || /\.svgz?$/i.test(file.name)
  const own = !img.naturalWidth || !img.naturalHeight ? (svg ? viewBoxOf(await file.text()) : null) : { w: img.naturalWidth, h: img.naturalHeight }
  if (!own) throw new Error('unreadable')
  const scale = Math.min(box.width / own.w, box.height / own.h, kind === 'banner' && !svg ? 1 : Infinity)
  const w = Math.max(1, Math.round(own.w * scale)), h = Math.max(1, Math.round(own.h * scale))
  const canvas = document.createElement('canvas')
  // the logo letterboxed in its square; the banner as wide and high as it came out
  canvas.width = kind === 'banner' ? w : SIDE; canvas.height = kind === 'banner' ? h : SIDE
  const ctx = canvas.getContext('2d')!
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(img, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h)
  const png = canvas.toDataURL('image/png')
  if (bytesOf(png) <= MAX_LOGO) return png
  ctx.globalCompositeOperation = 'destination-over'
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  for (const quality of [0.9, 0.75, 0.6]) {
    const jpeg = canvas.toDataURL('image/jpeg', quality)
    if (bytesOf(jpeg) <= MAX_LOGO) return jpeg
  }
  return canvas.toDataURL('image/jpeg', 0.4)
}

/** The texts of the two pictures a company sets */
const WORDS = {
  logo: { title: 'settings.logo.title', hint: 'settings.logo.hint', remove: 'settings.logo.remove', removeConfirm: 'settings.logo.removeConfirm', saved: 'settings.logo.saved', removed: 'settings.logo.removed', notSaved: 'settings.logo.notSaved' },
  banner: { title: 'settings.banner.title', hint: 'settings.banner.hint', remove: 'settings.banner.remove', removeConfirm: 'settings.banner.removeConfirm', saved: 'settings.banner.saved', removed: 'settings.banner.removed', notSaved: 'settings.banner.notSaved' },
} as const

/**
 * Setting a company's logo — or, with kind "banner", its banner logo (the topbar's picture instead of
 * its name): choose a picture (any kind the browser opens, up to 10 MB), scaled here and saved at once; remove it
 * to show the initial (the name) again. For the company's admins and for global admins.
 */
export function LogoField({ companyId, name, url, idPrefix, onChanged, kind = 'logo' }: {
  companyId: string; name: string; url: string | null | undefined; idPrefix: string
  onChanged: (url: string | null) => void
  kind?: 'logo' | 'banner'
}) {
  const t = useT()
  const w = WORDS[kind]
  const { success, error: toastError } = useToast()
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const failed = (e: unknown) => toastError(t(w.notSaved), (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.tryAgainMoment'))

  const choose = async (file: File | undefined) => {
    if (input.current) input.current.value = ''   // the same file can be chosen again
    if (!file) return
    // any picture (some, such as HEIC on Windows, come without a type): the browser tells whether it can open it
    if (file.type && !file.type.startsWith('image/')) { toastError(t(w.notSaved), t('settings.logo.wrongType')); return }
    if (file.size > MAX_FILE) { toastError(t(w.notSaved), t('settings.logo.tooLarge')); return }
    setBusy(true)
    try {
      const image = await scaleLogo(file, kind).catch(() => { throw new Error(t('settings.logo.unreadable')) })
      const r = kind === 'banner' ? await companiesApi.setBanner(companyId, image) : await companiesApi.setLogo(companyId, image)
      onChanged(r.data.data[kind])
      success(t(w.saved))
    } catch (e) {
      if (e instanceof Error && !(e as { response?: unknown }).response) toastError(t(w.notSaved), e.message)
      else failed(e)
    } finally { setBusy(false) }
  }
  const remove = async () => {
    if (!confirm(t(w.removeConfirm, { name }))) return
    setBusy(true)
    try {
      await (kind === 'banner' ? companiesApi.removeBanner(companyId) : companiesApi.removeLogo(companyId))
      onChanged(null)
      success(t(w.removed))
    } catch (e) { failed(e) } finally { setBusy(false) }
  }

  return (
    <div className="form-field" id={`${idPrefix}-${kind}`}>
      <div className="label">{t(w.title)}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {kind === 'banner'
          ? url && <img src={url} alt={name} className="pic-backdrop" style={{ maxHeight: 40, maxWidth: 240, objectFit: 'contain', ['--pic-bg' as string]: 'var(--bg-3)', borderRadius: 6, ...backdropStyle(url) }} />
          : <CompanyLogo url={url} name={name || '?'} size={56} />}
        <input ref={input} id={`${idPrefix}-${kind}-file`} type="file" accept="image/*" className="sr-only"
          onChange={e => void choose(e.target.files?.[0])} disabled={busy} />
        <label htmlFor={`${idPrefix}-${kind}-file`} className="btn btn-ghost btn-sm" style={{ cursor: busy ? 'default' : 'pointer' }} aria-disabled={busy}>
          {busy ? <span className="spinner" role="img" aria-label={t('common.working')} style={{ width: 12, height: 12 }} /> : <ImagePlus size={12} />}
          {' '}{url ? t('settings.logo.change') : t('settings.logo.choose')}
        </label>
        {url && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={remove} disabled={busy}>
            <Trash2 size={12} /> {t(w.remove)}
          </button>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 6 }}>{t(w.hint)}</div>
      {url && <BackdropFields companyId={companyId} kind={kind} url={url} idPrefix={`${idPrefix}-${kind}`} onChanged={onChanged} />}
    </div>
  )
}

type Theme = 'dark' | 'light'
/** A picture's backdrop for a theme as stored: 'default', '#rrggbb' or null */
const storedBackdrop = (url: string, theme: Theme): string | null => {
  let v: string | null = null
  try { v = new URL(url, 'http://x').searchParams.get(theme) } catch { /* no backdrop */ }
  return v === 'default' ? 'default' : v && /^[0-9a-f]{6}$/i.test(v) ? `#${v.toLowerCase()}` : null
}

/**
 * A picture's backdrops, saved at once: in dark mode none / warm white / an own colour, in light mode
 * none / the dashboard's dark / an own colour — for a logo that disappears in one of the two themes
 */
function BackdropFields({ companyId, kind, url, idPrefix, onChanged }: {
  companyId: string; kind: 'logo' | 'banner'; url: string; idPrefix: string; onChanged: (url: string | null) => void
}) {
  const t = useT()
  const { success, error: toastError } = useToast()
  const save = async (theme: Theme, value: string | null) => {
    try {
      const r = await companiesApi.setBackdrops(companyId, kind, theme === 'dark' ? { backdropDark: value } : { backdropLight: value })
      onChanged(r.data.data[kind])
      success(t('settings.backdrop.saved'))
    } catch (e) {
      toastError(t('settings.backdrop.notSaved'), (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.tryAgainMoment'))
    }
  }
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {(['dark', 'light'] as Theme[]).map(theme =>
          <BackdropChoice key={theme} theme={theme} id={`${idPrefix}-backdrop-${theme}`} value={storedBackdrop(url, theme)} onSave={v => void save(theme, v)} />)}
      </div>
      <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 4 }}>{t('settings.backdrop.help')}</div>
    </div>
  )
}

function BackdropChoice({ theme, id, value, onSave }: { theme: Theme; id: string; value: string | null; onSave: (v: string | null) => void }) {
  const t = useT()
  const modeOf = (v: string | null) => v === null ? 'none' : v === 'default' ? 'default' : 'own'
  const [mode, setMode] = useState(modeOf(value))
  const [colour, setColour] = useState(value && value !== 'default' ? value : DEFAULT_BACKDROP[theme])
  // what is saved, once the answer is in (or changed elsewhere)
  useEffect(() => { setMode(modeOf(value)); if (value && value !== 'default') setColour(value) }, [value])
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(timer.current), [])
  const pick = (m: string) => {
    setMode(m)
    onSave(m === 'none' ? null : m === 'default' ? 'default' : colour)
  }
  // a colour picker reports every step while dragging: saved once it rests
  const changeColour = (c: string) => {
    setColour(c)
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => onSave(c), 500)
  }
  return (
    <div className="form-field" style={{ marginBottom: 0 }}>
      <label htmlFor={id} className="label">{t(`settings.backdrop.${theme}`)}</label>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <select id={id} className="input" value={mode} onChange={e => pick(e.target.value)} style={{ maxWidth: 180 }}>
          <option value="none">{t('settings.backdrop.none')}</option>
          <option value="default">{t(`settings.backdrop.default.${theme}`)}</option>
          <option value="own">{t('settings.backdrop.own')}</option>
        </select>
        {mode === 'own' && (
          <input type="color" id={`${id}-colour`} aria-label={t(`settings.backdrop.colour.${theme}`)} value={colour}
            onChange={e => changeColour(e.target.value)} style={{ width: 40, height: 32, padding: 2, border: '1px solid var(--line)', borderRadius: 6, background: 'var(--bg-3)' }} />
        )}
      </div>
    </div>
  )
}
